/**
 * The ✎ tab editor (ADR 0027): the person's own chat tabs on this Account.
 * Tabs on the bar can be dragged (or moved with the arrows) and removed;
 * built-in tabs wait under "Add tabs", and the Account's OnlyFans lists are
 * ticked and added together (in list order, at the end of the bar).
 * Every change is saved at once; nothing is written to OnlyFans.
 *
 * Dragging uses pointer events (mouse, pen or finger alike), as the side
 * column editor does.
 */
import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useLibraryDeps } from '../library/LibraryDeps'
import { FeatherIcon } from '../components/FeatherIcon'
import { addTab, BUILTIN_TABS, moveTab, removeTab, tabKey, tabLabel, type TabRef } from './tabLayout'
import { pageAll, type ListTab } from './useChatTabs'
import { inApp } from '../theme/uiScale'
import { Spinner } from '../components/Spinner'

const DRAG_SLOP = 4
const BUILTIN_ICONS: Record<string, string> = { unread: 'mail', ai: 'cpu', needs_you: 'user-check', ignored: 'eye-off', activity: 'activity', suggestions: 'zap', active_chats: 'messages-square' }

interface Props {
  tabs: TabRef[]
  /** Saving works (the layout table exists and the CRM answered). */
  available: boolean
  /** The last save did not go through (the bar went back). */
  failed: boolean
  onChange: (tabs: TabRef[]) => void
  onReset: () => void
  /** OnlyFans' complete list of lists, so tabs of deleted lists can go. */
  onLists: (lists: ListTab[]) => void
  onClose: () => void
}

export function TabEditor({ tabs, available, failed, onChange, onReset, onLists, onClose }: Props) {
  const { t } = useTranslation('chats')
  const { gateway } = useLibraryDeps()
  const root = useRef<HTMLDivElement>(null)
  const press = useRef<{ key: string; x: number; y: number } | null>(null)
  const [drag, setDrag] = useState<{ key: string; x: number; y: number } | null>(null)
  const [drop, setDrop] = useState<{ area: 'bar'; index: number } | { area: 'add' } | null>(null)
  const [lists, setLists] = useState<ListTab[] | null>(null)
  const [listsFailed, setListsFailed] = useState(false)
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<string[]>([])

  const onListsRef = useRef(onLists)
  onListsRef.current = onLists
  useEffect(() => {
    let live = true
    pageAll(gateway, '/v1/lists/get', 20).then(
      (rows) => {
        if (!live) return
        setLists(rows)
        onListsRef.current(rows)
      },
      () => live && setListsFailed(true),
    )
    return () => {
      live = false
    }
  }, [gateway])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const onBar = new Set(tabs.map(tabKey))
  const builtinsToAdd = BUILTIN_TABS.filter((id) => !onBar.has(`builtin:${id}`))
  const offBar = useMemo(
    () => (lists ?? []).filter((l) => !onBar.has(`list:${l.id}`)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lists, tabs],
  )
  const listsToAdd = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? offBar.filter((l) => l.name.toLowerCase().includes(q)) : offBar
  }, [offBar, query])
  // Ticks survive a search; a list that reached the bar some other way drops out.
  const ticked = offBar.filter((l) => picked.includes(l.id))
  const allShownTicked = listsToAdd.length > 0 && listsToAdd.every((l) => picked.includes(l.id))
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))
  const tickShown = () =>
    setPicked((p) => (allShownTicked ? p.filter((id) => !listsToAdd.some((l) => l.id === id)) : [...new Set([...p, ...listsToAdd.map((l) => l.id)])]))
  const addTicked = () => {
    onChange(ticked.reduce((bar, l) => addTab(bar, { kind: 'list', id: l.id, name: l.name }), tabs))
    setPicked([])
  }

  const latest = useRef({ tabs, onChange, drop })
  latest.current = { tabs, onChange, drop }

  useEffect(() => {
    const move = (e: PointerEvent) => {
      const p = press.current
      if (!p || !root.current) return
      if (!drag && Math.hypot(e.clientX - p.x, e.clientY - p.y) < DRAG_SLOP) return
      e.preventDefault()
      setDrag({ key: p.key, x: e.clientX, y: e.clientY })
      setDrop(dropAt(root.current, p.key, e.clientX, e.clientY))
    }
    const up = () => {
      const p = press.current
      press.current = null
      const { tabs: now, onChange: save, drop: target } = latest.current
      const from = now.findIndex((x) => tabKey(x) === p?.key)
      if (p && drag && target && from >= 0) {
        if (target.area === 'add') save(removeTab(now, now[from]!))
        else save(moveTab(now, from, target.index))
      }
      setDrag(null)
      setDrop(null)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
  }, [drag])

  const startPress = (key: string) => (e: ReactPointerEvent) => {
    if (!available || e.button !== 0 || (e.target as HTMLElement).closest('button')) return
    e.preventDefault()
    press.current = { key, x: e.clientX, y: e.clientY }
  }
  const others = drag ? tabs.filter((x) => tabKey(x) !== drag.key) : tabs
  const lineBefore = (key: string) => Boolean(drag && drop?.area === 'bar' && tabKey(others[drop.index] ?? ({} as TabRef)) === key)
  const lineAtEnd = Boolean(drag && drop?.area === 'bar' && drop.index >= others.length)
  const label = (tab: TabRef) => tabLabel(tab, t)

  return (
    <div ref={root} className={`chats-tab-editor ${drag ? 'is-dragging' : ''}`} role="dialog" aria-label={t('picker.title')}>
      <div className="chats-tab-editor-head">
        <span className="chats-tab-editor-title">{t('picker.title')}</span>
        <button type="button" className="chats-panel-tool" aria-label={t('tabEditor.close')} title={t('tabEditor.close')} onClick={onClose}>
          <FeatherIcon name="x" size={16} />
        </button>
      </div>
      <div className="chats-tab-editor-body">
        <p className="chats-tab-editor-help">{t('tabEditor.help')}</p>
        {!available && <p className="chats-tab-editor-note">{t('tabEditor.unavailable')}</p>}
        {failed && <p className="chats-tab-editor-note is-error">{t('tabEditor.saveFailed')}</p>}

        <span className="chats-tab-editor-label">{t('tabEditor.onBar')}</span>
        <ul aria-label={t('tabEditor.onBar')} data-drop-area="bar" className={`chats-tab-editor-list ${drag && drop?.area === 'bar' ? 'is-over' : ''}`}>
          <li className="chats-tab-editor-item is-locked" aria-label={t('tabs.all')}>
            <FeatherIcon name="lock" size={13} className="chats-tab-editor-grip" />
            <span className="chats-tab-editor-name">
              {t('tabs.all')}
              <small>{t('tabEditor.allLocked')}</small>
            </span>
          </li>
          {tabs.map((tab, i) => {
            const key = tabKey(tab)
            return (
              <li
                key={key}
                aria-label={label(tab)}
                data-tab={key}
                onPointerDown={startPress(key)}
                className={[
                  'chats-tab-editor-item',
                  drag?.key === key ? 'is-dragging' : '',
                  lineBefore(key) ? 'drop-before' : '',
                  i === tabs.length - 1 && lineAtEnd ? 'drop-after' : '',
                ].join(' ')}
              >
                <FeatherIcon name="grip-vertical" size={14} className="chats-tab-editor-grip" />
                <FeatherIcon name={tab.kind === 'builtin' ? BUILTIN_ICONS[tab.id] ?? 'tag' : 'list'} size={13} />
                <span className="chats-tab-editor-name" translate={tab.kind === 'list' ? 'no' : undefined}>
                  {label(tab)}
                </span>
                <button type="button" className="chats-tab-editor-btn" aria-label={t('tabEditor.up', { tab: label(tab) })} title={t('tabEditor.up', { tab: label(tab) })} disabled={!available || i === 0} onClick={() => onChange(moveTab(tabs, i, i - 1))}>
                  <FeatherIcon name="chevron-up" size={14} />
                </button>
                <button
                  type="button"
                  className="chats-tab-editor-btn"
                  aria-label={t('tabEditor.down', { tab: label(tab) })}
                  title={t('tabEditor.down', { tab: label(tab) })}
                  disabled={!available || i === tabs.length - 1}
                  onClick={() => onChange(moveTab(tabs, i, i + 1))}
                >
                  <FeatherIcon name="chevron-down" size={14} />
                </button>
                <button type="button" className="chats-tab-editor-btn is-remove" aria-label={t('tabEditor.remove', { tab: label(tab) })} title={t('tabEditor.remove', { tab: label(tab) })} disabled={!available} onClick={() => onChange(removeTab(tabs, tab))}>
                  <FeatherIcon name="minus-circle" size={15} />
                </button>
              </li>
            )
          })}
        </ul>

        <span className="chats-tab-editor-label">{t('tabEditor.add')}</span>
        <ul aria-label={t('tabEditor.add')} data-drop-area="add" className={`chats-tab-editor-list chats-tab-editor-list--add ${drag && drop?.area === 'add' ? 'is-over' : ''}`}>
          {drag && <li className="chats-tab-editor-empty">{t('tabEditor.dropToRemove')}</li>}
          {!drag && !builtinsToAdd.length && <li className="chats-tab-editor-empty">{t('tabEditor.allBuiltinsAdded')}</li>}
          {!drag &&
            builtinsToAdd.map((id) => {
              const tab: TabRef = { kind: 'builtin', id }
              return (
                <li key={id} className="chats-tab-editor-item chats-tab-editor-item--add" aria-label={label(tab)}>
                  <FeatherIcon name={BUILTIN_ICONS[id] ?? 'tag'} size={13} />
                  <span className="chats-tab-editor-name">
                    {label(tab)}
                    <small>{t(`tabEditor.about.${id}`)}</small>
                  </span>
                  <AddButton label={label(tab)} disabled={!available} onClick={() => onChange(addTab(tabs, tab))} />
                </li>
              )
            })}
        </ul>

        {!drag && (
          <>
            <div className="chats-tab-editor-label-row">
              <span className="chats-tab-editor-label">{t('tabEditor.lists')}</span>
              {available && listsToAdd.length > 1 && (
                <button type="button" className="chats-tab-editor-link" onClick={tickShown}>
                  {t(allShownTicked ? 'tabEditor.clear' : 'tabEditor.selectAll')}
                </button>
              )}
            </div>
            <input
              type="search"
              className="chats-tab-editor-search"
              value={query}
              placeholder={t('tabEditor.searchLists')}
              aria-label={t('tabEditor.searchLists')}
              autoComplete="off"
              data-1p-ignore=""
              data-lpignore="true"
              onChange={(e) => setQuery(e.target.value)}
            />
            <ul aria-label={t('tabEditor.lists')} className="chats-tab-editor-list chats-tab-editor-list--add">
              {listsFailed && <li className="chats-tab-editor-empty">{t('picker.failed')}</li>}
              {!listsFailed && lists === null && <li className="chats-tab-editor-empty"><Spinner size={14} label={t('picker.loading')} /></li>}
              {lists !== null && !listsToAdd.length && <li className="chats-tab-editor-empty">{t(query ? 'tabEditor.noMatch' : 'tabEditor.allListsAdded')}</li>}
              {listsToAdd.map((l) => {
                const on = picked.includes(l.id)
                return (
                  <li key={l.id} className={`chats-tab-editor-item chats-tab-editor-item--add chats-tab-editor-item--pick ${on ? 'is-on' : ''}`}>
                    <label className="chats-tab-editor-pick">
                      <input type="checkbox" className="chats-tab-editor-check" checked={on} disabled={!available} onChange={() => toggle(l.id)} />
                      <span className="chats-tab-editor-name" translate="no">
                        {l.name}
                        <small>{t('picker.fans', { count: l.total })}</small>
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
          </>
        )}

        <button type="button" className="chats-tab-editor-reset" disabled={!available} onClick={onReset}>
          <FeatherIcon name="rotate-ccw" size={12} />
          {t('tabEditor.reset')}
        </button>
      </div>
      {!drag && ticked.length > 0 && (
        <div className="chats-tab-editor-foot">
          <span>{t('tabEditor.nSelected', { count: ticked.length })}</span>
          <button type="button" className="chats-tab-editor-link" onClick={() => setPicked([])}>
            {t('tabEditor.clear')}
          </button>
          <button type="button" className="chats-tab-editor-addbtn is-primary" disabled={!available} onClick={addTicked}>
            <FeatherIcon name="plus" size={13} />
            {t('tabEditor.addN', { count: ticked.length })}
          </button>
        </div>
      )}
      {drag && (
        <div className="chats-tab-editor-ghost" style={{ left: inApp(drag.x + 12), top: inApp(drag.y - 14) }} aria-hidden="true">
          {label(tabs.find((x) => tabKey(x) === drag.key) ?? ({ kind: 'list', id: '' } as TabRef))}
        </div>
      )}
    </div>
  )
}

function AddButton({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  const { t } = useTranslation('chats')
  return (
    <button type="button" className="chats-tab-editor-addbtn" aria-label={t('tabEditor.addOne', { tab: label })} title={t('tabEditor.addOne', { tab: label })} disabled={disabled} onClick={onClick}>
      <FeatherIcon name="plus" size={13} />
      {t('tabEditor.addShort')}
    </button>
  )
}

/** Where a drag would land: a gap among the other tabs on the bar, or back under "Add tabs". */
function dropAt(root: HTMLElement, key: string, x: number, y: number): { area: 'bar'; index: number } | { area: 'add' } | null {
  for (const list of root.querySelectorAll<HTMLElement>('[data-drop-area]')) {
    const box = list.getBoundingClientRect()
    if (x < box.left - 24 || x > box.right + 24 || y < box.top - 12 || y > box.bottom + 12) continue
    if (list.dataset.dropArea === 'add') return { area: 'add' }
    const rows = [...list.querySelectorAll<HTMLElement>('[data-tab]')].filter((r) => r.dataset.tab !== key)
    const index = rows.filter((r) => {
      const b = r.getBoundingClientRect()
      return y > b.top + b.height / 2
    }).length
    return { area: 'bar', index }
  }
  return null
}
